import { CheckIcon, TargetIcon, XIcon } from "lucide-react";
import type { KeyboardEvent, RefObject } from "react";

import { cn } from "~/lib/utils";
import { PopoverPopup, PopoverTrigger } from "../ui/popover";

export const GOAL_MAX_LENGTH = 500;

export function ProjectLeadGoalBadge() {
  return (
    <span
      className="inline-flex h-11 shrink-0 cursor-default items-center gap-1 rounded-sm border border-primary/30 bg-primary/10 px-2 text-[10px] font-semibold text-primary sm:h-4 sm:px-[calc(var(--spacing)*1-1px)] sm:text-[.625rem]"
      data-testid="chatheader-project-lead-badge"
      aria-label="Project Lead conversation"
      title="Project Lead task creation conversation"
    >
      <TargetIcon aria-hidden="true" className="size-3 shrink-0 opacity-70" />
      <span>Project Lead</span>
    </span>
  );
}

export function TaskManagedGoalBadge(props: { readonly goal: string | null | undefined }) {
  return (
    <span
      className="inline-flex h-11 max-w-[38vw] min-w-11 shrink-0 cursor-default items-center gap-1 truncate rounded-sm border border-input bg-background px-2 py-0 text-[10px] font-semibold text-foreground sm:h-4 sm:min-w-4 sm:max-w-[24vw] sm:px-[calc(var(--spacing)*1-1px)] sm:text-[.625rem] dark:bg-input/32"
      data-testid="chatheader-goal-badge"
      aria-label={props.goal ? `Task-managed goal: ${props.goal}` : "Task-managed goal is syncing"}
      title={
        props.goal
          ? `Managed by attached CMMD task\n\n${props.goal}`
          : "Managed by attached CMMD task"
      }
    >
      <TargetIcon aria-hidden="true" className="size-3 shrink-0 opacity-60" />
      <span className="truncate">{props.goal ?? "Task goal"}</span>
    </span>
  );
}

export function EditableGoalTrigger(props: { readonly goal: string | null | undefined }) {
  const hasGoal = props.goal != null && props.goal.length > 0;
  return hasGoal ? (
    <PopoverTrigger
      render={
        <button
          type="button"
          className={cn(
            "inline-flex shrink-0 cursor-pointer items-center gap-1 truncate rounded-sm",
            "border border-input bg-background px-2 py-0 text-[10px] font-semibold text-foreground sm:px-[calc(var(--spacing)*1-1px)] sm:text-xs",
            "h-11 max-w-[38vw] min-w-11 sm:h-4 sm:min-w-4 sm:max-w-[24vw] sm:text-[.625rem]",
            "transition-colors hover:bg-accent/50 dark:bg-input/32 dark:hover:bg-input/48",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
          )}
          data-testid="chatheader-goal-badge"
          aria-label={`Goal: ${props.goal}`}
          title={props.goal ?? undefined}
        />
      }
    >
      <TargetIcon aria-hidden="true" className="size-3 shrink-0 opacity-60" />
      <span className="truncate">{props.goal}</span>
    </PopoverTrigger>
  ) : (
    <PopoverTrigger
      render={
        <button
          type="button"
          className={cn(
            "inline-flex size-11 shrink-0 items-center justify-center rounded-lg sm:size-7",
            "text-muted-foreground/40 transition-colors",
            "hover:bg-muted/60 hover:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
          )}
          data-testid="chatheader-goal-set-button"
          aria-label="Set thread goal"
          title="Set thread goal"
        />
      }
    >
      <TargetIcon aria-hidden="true" className="size-3.5" />
    </PopoverTrigger>
  );
}

interface GoalEditorPopupProps {
  readonly textareaRef: RefObject<HTMLTextAreaElement | null>;
  readonly inputValue: string;
  readonly hasGoal: boolean;
  readonly nearLimit: boolean;
  readonly atLimit: boolean;
  readonly saving: boolean;
  readonly trimmedUnchanged: boolean;
  readonly onInputChange: (value: string) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  readonly onCommit: () => void;
  readonly onClear: () => void;
  readonly anchor?: HTMLElement | undefined;
}

export function GoalEditorPopup(props: GoalEditorPopupProps) {
  return (
    <PopoverPopup
      side="bottom"
      align="start"
      sideOffset={6}
      {...(props.anchor ? { anchor: props.anchor } : {})}
    >
      <div className="flex w-72 flex-col gap-2" role="dialog" aria-label="Edit thread goal">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="goal-textarea">
          Thread goal
        </label>
        <textarea
          ref={props.textareaRef}
          id="goal-textarea"
          className={cn(
            "min-h-8 w-full resize-none rounded-md border bg-transparent px-2 py-1.5 text-sm leading-snug outline-none",
            "placeholder:text-muted-foreground/50",
            "focus:ring-1 focus:ring-ring",
            props.atLimit ? "border-destructive/50 focus:ring-destructive/50" : undefined,
          )}
          placeholder="What should this thread accomplish?"
          value={props.inputValue}
          onChange={(event) => props.onInputChange(event.target.value)}
          onKeyDown={props.onKeyDown}
          rows={1}
          data-testid="chatheader-goal-input"
          disabled={props.saving}
          aria-describedby={props.nearLimit ? "goal-char-count" : undefined}
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              className={cn(
                "inline-flex min-h-11 items-center gap-1 rounded-md px-3 text-xs font-medium transition-colors sm:min-h-7 sm:px-2.5",
                "bg-primary text-primary-foreground hover:bg-primary/90",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                "disabled:pointer-events-none disabled:opacity-50",
              )}
              onClick={props.onCommit}
              disabled={props.saving || props.trimmedUnchanged}
              data-testid="chatheader-goal-save"
              aria-label="Save goal"
            >
              <CheckIcon aria-hidden="true" className="size-3" />
              Save
            </button>
            {props.hasGoal ? (
              <button
                type="button"
                className={cn(
                  "inline-flex min-h-11 items-center gap-1 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors sm:min-h-7 sm:px-2",
                  "hover:bg-destructive-surface hover:text-destructive",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                  "disabled:pointer-events-none disabled:opacity-50",
                )}
                onClick={props.onClear}
                disabled={props.saving}
                data-testid="chatheader-goal-clear"
                aria-label="Clear goal"
              >
                <XIcon aria-hidden="true" className="size-3" />
                Clear
              </button>
            ) : null}
          </div>
          {props.nearLimit ? (
            <span
              id="goal-char-count"
              className={cn(
                "text-[10px] tabular-nums",
                props.atLimit ? "text-destructive" : "text-muted-foreground/60",
              )}
            >
              {props.inputValue.length}/{GOAL_MAX_LENGTH}
            </span>
          ) : null}
        </div>
        <p className="text-[10px] text-muted-foreground/50">
          Enter to save · Shift+Enter for newline · Esc to cancel
        </p>
      </div>
    </PopoverPopup>
  );
}
